
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { DiffVisualizer } from './components/DiffVisualizer';
import AdUnit from './components/AdUnit';
import { AD_SLOTS, isAdSlotConfigured } from './config/ads';
import { FileDropZone, usePreventFileNavigation } from './components/FileDropZone';
import { LocalOnlyBadge } from './components/LocalOnlyBadge';
import { ThemeToggle } from './components/ThemeToggle';
import { ThemePreference, useTheme } from './hooks/useTheme';
import { FileLoadError, readResponseText, readTextFile } from './utils/fileLoader';
import { summarizeDifferences, smartMergeConflict, explainChange, refineFeedback } from './services/geminiService';
import { 
  Split, 
  ArrowRightLeft, 
  RotateCcw,
  Copy,
  Upload,
  Loader2,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  X,
  LifeBuoy,
  Bug,
  Lightbulb,
  Headset,
  Send,
  ChevronLeft,
  ChevronRight,
  Globe,
  Trash2,
  Download,
  FileCode,
  FileText,
  AlertTriangle,
  Zap,
  Lock,
  Search,
  Maximize2,
  Minimize2,
  Layout,
  Columns,
  Rows,
  Eye,
  EyeOff
} from 'lucide-react';
import { analyticsService as analytics } from './services/analyticsService';
import {
  buildMergedText,
  computeLineDiff,
  countChangedLines,
  MergeChoices,
  MergeKeep,
  resolveChunk,
  resolvedIndices,
  summarizeChunks,
} from './utils/diffEngine';
import { ExternalLink } from 'lucide-react';

// Keys written by the retired daily-limit / enterprise-key gate.
const LEGACY_STORAGE_KEYS = ['localdiff_usage', 'localdiff_exempt_key'];

// --- HELP MODAL ---
interface FeedbackModalProps {
  onClose: () => void;
  onSuccess: (msg: string) => void;
  theme: 'light' | 'dark';
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ onClose, onSuccess, theme }) => {
  const [type, setType] = useState<'bug' | 'feature' | 'support'>('feature');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const isDark = theme === 'dark';

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setIsSubmitting(true);
    await refineFeedback(message, type);
    await new Promise(r => setTimeout(r, 800));
    setIsSubmitting(false);
    setIsDone(true);
    setTimeout(() => {
      onSuccess(`Thank you! Your feedback helps us improve LocalDiff.`);
      onClose();
    }, 1200);
  };

  if (isDone) {
    return (
      <div className={`fixed inset-0 z-[120] flex items-center justify-center p-4 backdrop-blur-sm ${isDark ? 'bg-slate-950/40' : 'bg-slate-500/20'} animate-in fade-in duration-300`}>
        <div className={`w-full max-w-sm border rounded-3xl p-8 text-center shadow-2xl ${isDark ? 'bg-slate-900 border-emerald-500/30' : 'bg-white border-emerald-500/30'}`}>
          <div className="w-16 h-16 bg-emerald-500/20 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} />
          </div>
          <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Success!</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-[120] flex items-center justify-center p-4 backdrop-blur-md ${isDark ? 'bg-slate-950/60' : 'bg-slate-900/20'} animate-in fade-in duration-200`}>
      <div role="dialog" aria-modal="true" aria-labelledby="feedback-dialog-title" className={`relative w-full max-w-md border rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 duration-300 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className={`p-5 sm:p-6 border-b flex justify-between items-center ${isDark ? 'bg-slate-850/50 border-slate-800' : 'bg-slate-50 border-slate-100'}`}>
          <div className={`flex items-center gap-2 font-bold text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <LifeBuoy size={20} className="text-blue-500" aria-hidden />
            <span id="feedback-dialog-title">Support</span>
          </div>
          <button onClick={onClose} aria-label="Close dialog" className="text-slate-400 hover:text-blue-500 transition-all p-1 hover:rotate-90"><X size={20} aria-hidden /></button>
        </div>

        <div className="p-5 sm:p-6 flex flex-col gap-6 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-2">
            <label htmlFor="feedback-message" className="text-xs font-black uppercase tracking-widest text-slate-500">Feedback</label>
            <textarea id="feedback-message" autoFocus value={message} onChange={(e) => setMessage(e.target.value)} className={`w-full h-24 border rounded-xl p-4 text-sm outline-none resize-none transition-all ${isDark ? 'bg-slate-950 border-slate-700 text-slate-200' : 'bg-white border-slate-200 text-slate-900'}`} placeholder="Your message..." />
          </div>
        </div>

        <div className={`p-5 sm:p-6 border-t ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-100'}`}>
           <button onClick={handleSubmit} disabled={isSubmitting || !message.trim()} className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95">
             {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {isSubmitting ? 'Sending...' : 'Send Feedback'}
           </button>
        </div>
      </div>
    </div>
  );
};

// --- RESET CONFIRMATION ---
const ResetConfirmationModal: React.FC<{ onConfirm: () => void; onCancel: () => void; theme: 'light' | 'dark' }> = ({ onConfirm, onCancel, theme }) => {
  const isDark = theme === 'dark';
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);
  return (
    <div className={`fixed inset-0 z-[150] flex items-center justify-center p-4 backdrop-blur-md ${isDark ? 'bg-slate-950/70' : 'bg-slate-900/30'} animate-in fade-in duration-200`}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="reset-dialog-title" aria-describedby="reset-dialog-desc" className={`relative w-full max-w-sm border rounded-3xl shadow-2xl p-8 flex flex-col gap-6 animate-in zoom-in-95 duration-200 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'}`}>
        <div className="text-center flex flex-col items-center gap-4">
          <div className="p-4 bg-red-500/10 text-red-500 rounded-full"><AlertTriangle size={32} /></div>
          <h3 id="reset-dialog-title" className={`text-xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>Reset Workspace?</h3>
          <p id="reset-dialog-desc" className="text-sm text-slate-500">This will clear all content.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} autoFocus className={`flex-1 py-3 font-bold rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>Cancel</button>
          <button onClick={onConfirm} className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg transition-all active:scale-95">Reset</button>
        </div>
      </div>
    </div>
  );
}

// --- WINDOW HEADER ---
interface WindowHeaderProps {
  title: string;
  icon: React.ComponentType<{ size?: number; className?: string; 'aria-hidden'?: boolean }>;
  isMaximized: boolean;
  onToggleMaximize: () => void;
  theme: 'light' | 'dark';
}

const WindowHeader: React.FC<WindowHeaderProps> = ({ title, icon: Icon, isMaximized, onToggleMaximize, theme }) => {
  const isDark = theme === 'dark';
  return (
    <div className={`h-10 px-4 border-b flex items-center justify-between select-none shrink-0 ${isDark ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-100 border-slate-200 text-slate-600'}`}>
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-blue-500" aria-hidden />
        <h2 className="text-[10px] font-black uppercase tracking-widest">{title}</h2>
      </div>
      <button
        onClick={onToggleMaximize}
        className="p-1 hover:bg-white/10 rounded transition-all"
        title={isMaximized ? 'Restore' : 'Maximize'}
        aria-label={`${isMaximized ? 'Restore' : 'Maximize'} ${title}`}
        aria-pressed={isMaximized}
      >
        {isMaximized ? <Minimize2 size={12} aria-hidden /> : <Maximize2 size={12} aria-hidden />}
      </button>
    </div>
  );
};

// --- APP COMPONENT ---
const App: React.FC = () => {
  const { preference: themePreference, resolvedTheme: theme, setPreference: setThemePreference } = useTheme();
  usePreventFileNavigation();

  const [leftText, setLeftText] = useState('');
  const [rightText, setRightText] = useState('');
  const [leftUrl, setLeftUrl] = useState('');
  const [rightUrl, setRightUrl] = useState('');
  const [fetchingLeft, setFetchingLeft] = useState(false);
  const [fetchingRight, setFetchingRight] = useState(false);

  const [mergedChoices, setMergedChoices] = useState<MergeChoices>({});
  const [mode, setMode] = useState<'edit' | 'compare'>('edit');
  const [layout, setLayout] = useState<'split' | 'unified'>('split');
  
  // COMPARISON OPTIONS
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false);
  const [ignoreCase, setIgnoreCase] = useState(false);
  const [ignoreBlankLines, setIgnoreBlankLines] = useState(false);

  // WINDOW CONFIG STATE
  const [showMergePanel, setShowMergePanel] = useState(false);
  const [mergedPanelHeight, setMergedPanelHeight] = useState(180); // px
  const [visibleWindows, setVisibleWindows] = useState({ original: true, modified: true, merged: true });
  const [maximizedWindow, setMaximizedWindow] = useState<string | null>(null);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [toast, setToast] = useState<{ message: string, type: 'error' | 'success' | 'info' } | null>(null);
  
  const [activeDiffIndex, setActiveDiffIndex] = useState(1);
  const diffScrollRef = useRef<HTMLDivElement>(null);
  const leftTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const rightTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const isSyncingScroll = useRef(false);
  const isResizing = useRef(false);

  const leftFileRef = useRef<HTMLInputElement>(null);
  const rightFileRef = useRef<HTMLInputElement>(null);

  const isDark = theme === 'dark';

  useEffect(() => {
    try {
      LEGACY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch {
      // Storage blocked: nothing to clean up.
    }
  }, []);

  const handleThemeChange = (next: ThemePreference) => {
    setThemePreference(next);
    analytics.trackFeatureUsage({ featureName: 'theme', action: next });
  };

  // Toasts announce themselves (role=status) and clear after a few seconds.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const loadFileInto = async (side: 'left' | 'right', file: File) => {
    try {
      const loaded = await readTextFile(file);
      (side === 'left' ? setLeftText : setRightText)(loaded.text);
      analytics.trackFileUpload({ fileName: loaded.name, fileSize: loaded.size, fileType: file.type });
    } catch (err) {
      setToast({ message: err instanceof FileLoadError ? err.message : 'Could not read that file.', type: 'error' });
      analytics.trackFeatureUsage({ featureName: 'file_load', action: err instanceof FileLoadError ? err.code : 'read-failed' });
    }
  };

  /** Two files at once fill both panels; a single file goes to the panel it was dropped on. */
  const handleFiles = (side: 'left' | 'right', files: File[]) => {
    if (files.length >= 2) {
      void loadFileInto('left', files[0]);
      void loadFileInto('right', files[1]);
      analytics.trackFeatureUsage({ featureName: 'file_load', action: 'pair' });
    } else if (files[0]) {
      void loadFileInto(side, files[0]);
    }
  };

  const resizeMergedPanel = (delta: number) => {
    setMergedPanelHeight(h => Math.max(100, Math.min(window.innerHeight - 200, h + delta)));
  };

  const allChanges = useMemo(
    () => computeLineDiff(leftText, rightText, { ignoreWhitespace, ignoreCase, ignoreBlankLines }),
    [leftText, rightText, ignoreWhitespace, ignoreCase, ignoreBlankLines]
  );

  const diffChunksMetadata = useMemo(() => summarizeChunks(allChanges), [allChanges]);

  // Merge choices are keyed by change index, so they are only valid for the diff they were made on.
  useEffect(() => {
    setMergedChoices({});
  }, [allChanges]);

  // Auto-expand merged panel when comparing or merging
  useEffect(() => {
    if (mode === 'compare' && diffChunksMetadata.length > 0) {
      setShowMergePanel(true);
    }
  }, [mode, diffChunksMetadata.length]);

  const fullMergedText = useMemo(() => buildMergedText(allChanges, mergedChoices), [allChanges, mergedChoices]);
  const mergedIndexSet = useMemo(() => resolvedIndices(mergedChoices), [mergedChoices]);

  // Resize Handler
  const startResizing = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', stopResizing);
  }, []);

  const handleResize = useCallback((e: MouseEvent) => {
    if (!isResizing.current) return;
    const offset = window.innerHeight - e.clientY;
    setMergedPanelHeight(Math.max(100, Math.min(window.innerHeight - 200, offset)));
  }, []);

  const stopResizing = useCallback(() => {
    isResizing.current = false;
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', stopResizing);
  }, [handleResize]);

  const handleDiffNavigation = (newIndex: number) => {
    const clampedIndex = Math.max(1, Math.min(diffChunksMetadata.length, newIndex));
    setActiveDiffIndex(clampedIndex);
    const metadata = diffChunksMetadata[clampedIndex - 1];
    if (metadata) {
      const element = document.getElementById(`diff-chunk-${metadata.index}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-4', 'ring-blue-500/50', 'bg-blue-500/5');
        setTimeout(() => element.classList.remove('ring-4', 'ring-blue-500/50', 'bg-blue-500/5'), 1500);
      }
    }
  };

  // Keyboard shortcuts for diff navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (mode !== 'compare') return;
      if (e.altKey && e.key === 'ArrowDown') {
        e.preventDefault();
        handleDiffNavigation(activeDiffIndex + 1);
      } else if (e.altKey && e.key === 'ArrowUp') {
        e.preventDefault();
        handleDiffNavigation(activeDiffIndex - 1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode, activeDiffIndex, diffChunksMetadata]);

  const handleTextAreaScroll = (side: 'left' | 'right') => {
    if (isSyncingScroll.current) return;
    const source = side === 'left' ? leftTextAreaRef.current : rightTextAreaRef.current;
    const target = side === 'left' ? rightTextAreaRef.current : leftTextAreaRef.current;
    if (source && target) {
      isSyncingScroll.current = true;
      target.scrollTop = source.scrollTop;
      target.scrollLeft = source.scrollLeft;
      setTimeout(() => { isSyncingScroll.current = false; }, 50);
    }
  };

  const fetchContentFromUrl = async (url: string, side: 'left' | 'right') => {
    if (!url) return;
    const setter = side === 'left' ? setLeftText : setRightText;
    const fetcher = side === 'left' ? setFetchingLeft : setFetchingRight;
    fetcher(true);
    const startTime = Date.now();
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error();
      setter(await readResponseText(response));
      setToast({ message: "Content loaded.", type: 'success' });
      analytics.trackUrlFetch({ url, success: true, responseTime: Date.now() - startTime });
    } catch (err) {
      setToast({ message: err instanceof FileLoadError ? err.message : "Failed to fetch. Check CORS.", type: 'error' });
      analytics.trackUrlFetch({ url, success: false });
    } finally {
      fetcher(false);
    }
  };

  const handleMergeAction = (index: number, keep: MergeKeep) => {
    setMergedChoices(prev => resolveChunk(allChanges, prev, index, keep));
    setShowMergePanel(true);
    analytics.trackFeatureUsage({ featureName: 'merge', action: keep === 'original' ? 'keep_original' : 'accept_modified' });
  };

  const handleToggleMode = () => {
    if (mode === 'compare') { 
      setMode('edit'); 
      setMaximizedWindow(null); 
      return; 
    }
    if (!leftText.trim() && !rightText.trim()) { 
      setToast({ message: "Paste content first.", type: "info" }); 
      return; 
    }

    setActiveDiffIndex(diffChunksMetadata.length > 0 ? 1 : 0);
    setMode('compare');
    setMergedChoices({});
    setLayout('split');
    analytics.trackComparison({ leftLength: leftText.length, rightLength: rightText.length, mode: layout, hasAiAnalysis: false });
  };

  const handleDownloadMerged = () => {
    const blob = new Blob([fullMergedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `localdiff-merged-${new Date().getTime()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    analytics.trackReportDownload({ format: 'html', comparisonsInReport: 0 });
  };

  const handleDownloadReport = () => {
    const timestamp = new Date().toLocaleString();
    const totalChanges = diffChunksMetadata.length;
    const { added, removed } = countChangedLines(allChanges);
    
    let diffHtml = '';
    for (const change of allChanges) {
      const lines = change.value.split('\n').filter((_, i, arr) => i < arr.length - 1 || arr[arr.length - 1] !== '');
      for (const line of lines) {
        const escaped = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        if (change.added) {
          diffHtml += `<tr class="added"><td>+</td><td>${escaped}</td></tr>`;
        } else if (change.removed) {
          diffHtml += `<tr class="removed"><td>-</td><td>${escaped}</td></tr>`;
        } else {
          diffHtml += `<tr><td>&nbsp;</td><td>${escaped}</td></tr>`;
        }
      }
    }
    
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>LocalDiff Report - ${timestamp}</title>
<style>body{font-family:system-ui,sans-serif;max-width:960px;margin:0 auto;padding:20px;background:#0f172a;color:#e2e8f0}
h1{color:#3b82f6;font-size:1.5rem}
.stats{display:flex;gap:16px;margin:16px 0;font-size:13px}
.stats span{padding:4px 12px;border-radius:8px;font-weight:700}
.stat-add{background:#064e3b;color:#6ee7b7}
.stat-rm{background:#7f1d1d;color:#fca5a5}
.stat-total{background:#1e3a5f;color:#93c5fd}
table{width:100%;border-collapse:collapse;font-family:monospace;font-size:12px;margin-top:16px}
td{padding:2px 8px;white-space:pre-wrap;word-break:break-all}
td:first-child{width:20px;text-align:center;color:#64748b;user-select:none}
tr.added{background:#064e3b;color:#6ee7b7}
tr.removed{background:#7f1d1d;color:#fca5a5}
tr:hover{opacity:0.85}
footer{margin-top:32px;padding-top:16px;border-top:1px solid #334155;font-size:11px;color:#64748b;text-align:center}
</style></head><body>
<h1>LocalDiff.dev - Diff Report</h1>
<p style="color:#94a3b8;font-size:13px">Generated: ${timestamp}</p>
<div class="stats">
<span class="stat-total">${totalChanges} changes</span>
<span class="stat-add">+${added} additions</span>
<span class="stat-rm">-${removed} removals</span>
</div>
<table><tbody>${diffHtml}</tbody></table>
<footer>Generated by LocalDiff.dev &mdash; All comparisons run locally in your browser</footer>
</body></html>`;
    
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `localdiff-report-${new Date().getTime()}.html`;
    link.click();
    URL.revokeObjectURL(url);
    analytics.trackReportDownload({ format: 'html', comparisonsInReport: diffChunksMetadata.length });
  };

  const renderSourcePanel = (side: 'left' | 'right') => {
    const text = side === 'left' ? leftText : rightText;
    const url = side === 'left' ? leftUrl : rightUrl;
    const setUrl = side === 'left' ? setLeftUrl : setRightUrl;
    const fileRef = side === 'left' ? leftFileRef : rightFileRef;
    const fetching = side === 'left' ? fetchingLeft : fetchingRight;
    const setText = side === 'left' ? setLeftText : setRightText;
    const id = side === 'left' ? 'original' : 'modified';
    const label = side === 'left' ? 'Original' : 'Modified';
    const title = `${label} Source`;

    if (!visibleWindows[id] && maximizedWindow !== id) return null;
    if (maximizedWindow && maximizedWindow !== id) return null;

    return (
      <article
        aria-label={title}
        className={`flex flex-col flex-1 min-h-0 border rounded-xl overflow-hidden transition-all duration-300 ${isDark ? 'bg-slate-900 border-slate-800 shadow-2xl shadow-black/50' : 'bg-white border-slate-200 shadow-xl'}`}
      >
        <WindowHeader
          title={title}
          icon={side === 'left' ? FileText : FileCode}
          isMaximized={maximizedWindow === id}
          onToggleMaximize={() => setMaximizedWindow(maximizedWindow === id ? null : id)}
          theme={theme}
        />
        <div className={`p-3 border-b flex gap-2 ${isDark ? 'bg-slate-950/20' : 'bg-slate-50/50'}`}>
           <button onClick={() => fileRef.current?.click()} className="p-2 border rounded-lg hover:bg-blue-500/10 transition-all" title="Open local file (select two to fill both panels)" aria-label={`Open a local file into ${label}`}><Upload size={14} aria-hidden /></button>
           <input type="file" ref={fileRef} hidden multiple tabIndex={-1} onChange={(e) => {
             handleFiles(side, Array.from(e.target.files ?? []));
             e.target.value = ''; // allow re-selecting the same file
           }} />
           <div className="relative flex-grow group">
             <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={12} aria-hidden />
             <input 
                type="url" 
                aria-label={`URL to load into ${label}`}
                placeholder="Paste URL..." 
                value={url} 
                onChange={(e) => setUrl(e.target.value)} 
                onKeyDown={(e) => e.key === 'Enter' && fetchContentFromUrl(url, side)} 
                className={`w-full pl-8 pr-16 py-1.5 text-[11px] rounded-lg border outline-none transition-all ${isDark ? 'bg-slate-950 border-slate-700 text-slate-200 focus:border-blue-500' : 'bg-white border-slate-200 text-slate-900 focus:border-blue-500'}`} 
              />
              <button 
                onClick={() => fetchContentFromUrl(url, side)}
                disabled={fetching || !url}
                aria-label={`Fetch URL into ${label}`}
                className="absolute right-1 top-1/2 -translate-y-1/2 px-2.5 py-1 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-500 disabled:opacity-30 text-white text-[9px] font-black rounded-md transition-all active:scale-95 flex items-center gap-1.5"
              >
                {fetching ? <Loader2 size={10} className="animate-spin" aria-hidden /> : 'FETCH'}
              </button>
           </div>
        </div>
        <FileDropZone
          theme={theme}
          label={label}
          onFiles={(files) => handleFiles(side, files)}
          onText={setText}
          className="flex-grow min-h-0 overflow-hidden"
        >
          {fetching && (
            <div className={`absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[2px] ${isDark ? 'bg-slate-950/40' : 'bg-white/40'}`}>
               <div role="status" className="flex items-center gap-3 px-4 py-2 rounded-full border bg-white shadow-xl text-blue-600 font-bold text-xs">
                  <Loader2 size={14} className="animate-spin" aria-hidden />
                  <span>Fetching remote data...</span>
               </div>
            </div>
          )}
          <textarea 
            ref={side === 'left' ? leftTextAreaRef : rightTextAreaRef} 
            aria-label={`${label} text`}
            spellCheck={false}
            value={text} 
            onChange={(e) => setText(e.target.value)} 
            onScroll={() => handleTextAreaScroll(side)} 
            placeholder="Paste text, or drop a file here..." 
            className={`w-full h-full p-4 outline-none font-mono text-xs leading-relaxed resize-none transition-all ${isDark ? 'bg-transparent text-slate-200' : 'bg-white text-slate-900'}`} 
          />
        </FileDropZone>
      </article>
    );
  };

  return (
    <div className={`flex flex-col h-screen overflow-hidden ${isDark ? 'dark bg-slate-950 text-slate-200' : 'bg-slate-50 text-slate-800'}`}>
      {toast && (
        <div className="fixed top-20 right-4 z-[200] toast animate-slide-down" role={toast.type === 'error' ? 'alert' : 'status'}>
          <div className={`px-5 py-3 rounded-xl border shadow-2xl flex items-center gap-3 font-bold text-sm ${toast.type === 'error' ? 'bg-red-950 border-red-500 text-red-200' : 'bg-emerald-950 border-emerald-500 text-emerald-200'}`}>
            <span className="max-w-xs">{toast.message}</span>
            <button onClick={() => setToast(null)} aria-label="Dismiss notification"><X size={14} aria-hidden /></button>
          </div>
        </div>
      )}

      {showFeedbackModal && <FeedbackModal onClose={() => setShowFeedbackModal(false)} onSuccess={(m) => setToast({ message: m, type: 'success' })} theme={theme} />}
      {showResetConfirm && <ResetConfirmationModal onConfirm={() => { setLeftText(''); setRightText(''); setMode('edit'); setShowResetConfirm(false); }} onCancel={() => setShowResetConfirm(false)} theme={theme} />}

      <header className={`min-h-16 py-2 border-b flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 sm:px-6 shrink-0 z-50 glass-header ${isDark ? 'bg-slate-900/80 border-slate-700/60 shadow-lg' : 'bg-white/80 border-slate-200/80 shadow-sm'}`}>
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="hidden sm:block p-2 bg-blue-600 rounded-lg shadow-lg" aria-hidden><Split className="text-white" size={20} /></div>
          <h1 className="flex flex-col leading-none">
            <span className="text-xl font-black tracking-tighter bg-gradient-to-r from-blue-500 to-emerald-500 bg-clip-text text-transparent">LocalDiff.dev</span>
            <span className={`hidden md:block text-[10px] font-bold tracking-wide mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Secure local diff tool</span>
          </h1>
          <LocalOnlyBadge theme={theme} />
          {mode === 'compare' && (
            <div className="hidden sm:flex items-center gap-2 ml-4 px-3 py-1.5 border rounded-xl bg-blue-500/5 border-blue-500/20">
              <span className="text-[10px] font-black uppercase tracking-widest text-blue-500">Workspace:</span>
              <button onClick={() => setVisibleWindows(p => ({ ...p, merged: !p.merged }))} className={`p-1 rounded transition-all ${visibleWindows.merged ? 'text-blue-500 bg-blue-500/10' : 'text-slate-400'}`} title="Toggle Merged Results" aria-label="Show merged results panel" aria-pressed={visibleWindows.merged}><Eye size={14} aria-hidden /></button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 ml-auto">
          <ThemeToggle preference={themePreference} onChange={handleThemeChange} theme={theme} />
          <div className="flex items-center gap-2">
             <button onClick={() => setShowFeedbackModal(true)} className="px-4 py-2 rounded-xl text-xs font-bold border hover:bg-blue-500/10 transition-all">Support</button>
             <button onClick={() => setShowResetConfirm(true)} className="px-4 py-2 rounded-xl text-xs font-bold border text-red-500 hover:bg-red-500/10 transition-all">Reset</button>
          </div>
          {mode === 'compare' && (
            <button onClick={handleToggleMode} className="flex items-center gap-2 px-5 py-2.5 bg-slate-800 text-slate-100 rounded-xl font-bold text-xs hover:scale-105 active:scale-95 transition-all shadow-xl">
              <ArrowRightLeft size={16} /> Edit
            </button>
          )}
        </div>
      </header>

      {isAdSlotConfigured(AD_SLOTS.header) && (
        <AdUnit slotId={AD_SLOTS.header} format="horizontal" className="px-6 pt-2" theme={theme} label="Sponsored" />
      )}

      <main id="main-content" tabIndex={-1} className="flex-grow min-h-0 flex flex-col p-4 sm:p-6 gap-4 overflow-hidden relative focus:outline-none">
        {mode === 'edit' && (
          <div className="flex-grow min-h-0 flex flex-col sm:flex-row gap-4 overflow-hidden">
            {renderSourcePanel('left')}
            {renderSourcePanel('right')}
            <div className="fixed bottom-10 left-1/2 -translate-x-1/2">
              <button onClick={handleToggleMode} className="btn-compare flex items-center gap-3 px-12 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black shadow-2xl transition-all hover:scale-105 active:scale-95 border border-blue-400/30">
                <Search size={24} /> Compare Sources
              </button>
            </div>
          </div>
        )}

        {mode === 'compare' && (
          <div className="flex-grow flex flex-col gap-4 overflow-hidden">
             {/* COMPARE TOOLBAR */}
             <div className={`flex flex-wrap items-center gap-4 px-5 py-3 border rounded-2xl shadow-lg shrink-0 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
                <div className="flex gap-1 bg-slate-500/5 p-1 rounded-xl">
                   <button onClick={() => handleDiffNavigation(activeDiffIndex - 1)} disabled={activeDiffIndex <= 1} aria-label="Previous difference (Alt+Up)" title="Previous difference (Alt+Up)" className="p-2 border rounded-lg hover:bg-blue-500/10 disabled:opacity-30 transition-all"><ChevronLeft size={16} aria-hidden /></button>
                   <button onClick={() => handleDiffNavigation(activeDiffIndex + 1)} disabled={activeDiffIndex >= diffChunksMetadata.length} aria-label="Next difference (Alt+Down)" title="Next difference (Alt+Down)" className="p-2 border rounded-lg hover:bg-blue-500/10 disabled:opacity-30 transition-all"><ChevronRight size={16} aria-hidden /></button>
                </div>
                
                <div className="flex items-center gap-2 flex-grow max-w-sm">
                  <div className="relative flex-grow">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                    <select aria-label="Jump to difference" value={activeDiffIndex} onChange={(e) => handleDiffNavigation(parseInt(e.target.value))} className={`w-full h-10 pl-9 pr-10 border rounded-xl text-xs font-bold transition-all appearance-none outline-none ${isDark ? 'bg-slate-950 border-slate-700 text-slate-300 focus:border-blue-500' : 'bg-slate-50 border-slate-200 text-slate-700 focus:border-blue-500'}`}>
                      {diffChunksMetadata.length > 0 ? diffChunksMetadata.map((chunk, idx) => <option key={idx} value={idx + 1}>#{idx + 1} | {chunk.summary}</option>) : <option>Identical</option>}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                  </div>
                </div>

                <div className="flex gap-2 bg-slate-500/5 p-1 rounded-xl" role="group" aria-label="Diff layout">
                  <button onClick={() => setLayout('split')} aria-pressed={layout === 'split'} className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${layout === 'split' ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'text-slate-500 hover:text-blue-500'}`}>Split</button>
                  <button onClick={() => setLayout('unified')} aria-pressed={layout === 'unified'} className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${layout === 'unified' ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'text-slate-500 hover:text-blue-500'}`}>Unified</button>
                </div>

                <div className={`flex items-center gap-3 px-3 py-1.5 border rounded-xl text-[10px] font-bold ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <label className={`flex items-center gap-1.5 cursor-pointer select-none ${ignoreWhitespace ? 'text-blue-500' : 'text-slate-500'}`}>
                    <input type="checkbox" title="Ignore leading and trailing whitespace" checked={ignoreWhitespace} onChange={(e) => setIgnoreWhitespace(e.target.checked)} className="w-3 h-3 accent-blue-600" /> Whitespace
                  </label>
                  <label className={`flex items-center gap-1.5 cursor-pointer select-none ${ignoreCase ? 'text-blue-500' : 'text-slate-500'}`}>
                    <input type="checkbox" checked={ignoreCase} onChange={(e) => setIgnoreCase(e.target.checked)} className="w-3 h-3 accent-blue-600" /> Case
                  </label>
                  <label className={`flex items-center gap-1.5 cursor-pointer select-none ${ignoreBlankLines ? 'text-blue-500' : 'text-slate-500'}`}>
                    <input type="checkbox" checked={ignoreBlankLines} onChange={(e) => setIgnoreBlankLines(e.target.checked)} className="w-3 h-3 accent-blue-600" /> Blanks
                  </label>
                </div>
                
                <div className="ml-auto hidden lg:flex items-center gap-3">
                  <button onClick={handleDownloadReport} className="flex items-center gap-2 px-4 py-2 border rounded-xl text-xs font-bold hover:bg-blue-500/10 transition-all"><FileText size={14} /> Report</button>
                  <div className="flex items-center gap-2 text-[10px] font-black text-emerald-500 bg-emerald-500/10 px-4 py-2 rounded-full border border-emerald-500/20 shadow-inner">
                    <Lock size={12} /> SECURE
                  </div>
                </div>
             </div>

             <div className="flex-grow min-h-0 flex flex-col gap-4 overflow-hidden">
                <div className="flex flex-grow min-h-0 gap-4 overflow-hidden relative">
                   {/* DYNAMIC WINDOW CONTAINER - Only Diff Vis is shown here now */}
                   <div className="flex-grow min-h-0 flex gap-4 overflow-hidden">
                      <div className={`flex flex-col flex-grow transition-all duration-300 overflow-hidden relative border rounded-xl shadow-inner ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-white border-slate-200'}`}>
                         <WindowHeader title="Diff Visualization" icon={Split} isMaximized={maximizedWindow === 'diff'} onToggleMaximize={() => setMaximizedWindow(maximizedWindow === 'diff' ? null : 'diff')} theme={theme} />
                         <div ref={diffScrollRef} className="flex-grow overflow-auto p-4">
                            <DiffVisualizer original={leftText} modified={rightText} changes={allChanges} onMerge={handleMergeAction} viewMode={layout} mergedChunkIndices={mergedIndexSet} theme={theme} activeDiffIndex={activeDiffIndex} />
                         </div>
                      </div>
                   </div>
                </div>

                {/* RESIZABLE MERGED PANEL */}
                {visibleWindows.merged && (
                  <div 
                    className={`flex flex-col shrink-0 border rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 relative ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}
                    style={{ height: showMergePanel ? `${mergedPanelHeight}px` : '40px' }}
                  >
                     {/* Resize Handle */}
                     <div
                       role="separator"
                       aria-orientation="horizontal"
                       aria-label="Resize merged results (Up/Down arrows)"
                       aria-valuenow={mergedPanelHeight}
                       aria-valuemin={100}
                       tabIndex={0}
                       onMouseDown={startResizing}
                       onKeyDown={(e) => {
                         if (e.key === 'ArrowUp') { e.preventDefault(); resizeMergedPanel(20); }
                         if (e.key === 'ArrowDown') { e.preventDefault(); resizeMergedPanel(-20); }
                       }}
                       className="absolute top-0 left-0 right-0 h-1 cursor-row-resize hover:bg-blue-500/50 focus:bg-blue-500/70 focus:outline-none transition-all z-50"
                     />
                     
                     <div className="h-10 px-6 flex items-center justify-between border-b shrink-0 select-none">
                        <div className="flex items-center gap-3 text-[10px] font-black text-blue-500 uppercase tracking-widest">
                           <FileCode size={14} aria-hidden /> <h2>Merged Results</h2>
                           <button onClick={() => setShowMergePanel(!showMergePanel)} aria-expanded={showMergePanel} aria-label={showMergePanel ? 'Collapse merged results' : 'Expand merged results'} className="p-1 hover:scale-110 transition-all text-slate-400">
                             {showMergePanel ? <ChevronDown size={18} aria-hidden /> : <ChevronUp size={18} aria-hidden />}
                           </button>
                        </div>
                        <div className="flex items-center gap-4">
                           <button onClick={handleDownloadMerged} className="text-[10px] font-bold text-emerald-500 flex items-center gap-2 hover:scale-105 transition-all"><Download size={14} /> Download</button>
                           <button onClick={() => { navigator.clipboard.writeText(fullMergedText); setToast({message: 'Copied', type: 'success'})}} className="text-[10px] font-bold text-blue-500 flex items-center gap-2 hover:scale-105 transition-all"><Copy size={14} /> Copy</button>
                           <button onClick={() => { setMergedChoices({}); setShowMergePanel(false); }} className="text-[10px] font-bold text-red-400 flex items-center gap-2 transition-all"><RotateCcw size={14} /> Reset</button>
                        </div>
                     </div>
                     {showMergePanel && (
                       <textarea 
                         aria-label="Merged result"
                         className={`flex-grow p-6 font-mono text-xs outline-none resize-none leading-relaxed transition-all ${isDark ? 'bg-slate-950/50 text-slate-200' : 'bg-slate-50 text-slate-900'}`} 
                         value={fullMergedText} 
                         readOnly 
                         placeholder="Final code will appear here..."
                       />
                     )}
                  </div>
                )}
             </div>
          </div>
        )}
      </main>

      {/* Sponsored tools strip — always visible */}
      <div className={`shrink-0 border-t flex items-center gap-3 flex-wrap px-6 py-2 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
        <span className={`text-[9px] font-black uppercase tracking-widest mr-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Open source tools</span>
        {([
          { name: 'TFOverrides', url: 'https://tfoverrides.dev/' },
          { name: 'kctl-env', url: 'https://github.com/senet/kctl-env' },
          { name: 'gar-cleanup', url: 'https://github.com/senet/gar-cleanup' },
          { name: 'helm-map', url: 'https://github.com/senet/helm-map' },
        ] as const).map(tool => (
          <a
            key={tool.name}
            href={tool.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-[10px] font-bold transition-all hover:border-blue-500 hover:text-blue-400 active:scale-95 ${isDark ? 'border-slate-700 text-slate-300 bg-slate-800' : 'border-slate-300 text-slate-600 bg-white'}`}
            onClick={() => analytics.trackAffiliateClick({ affiliateId: tool.name, affiliate: tool.name, category: 'development-tools' })}
          >
            {tool.name} <ExternalLink size={9} />
          </a>
        ))}
      </div>

      <footer className={`border-t flex items-center justify-between px-6 py-2 text-[9px] font-bold text-slate-500 shrink-0 ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-white border-slate-100'}`}>
        <div className="flex items-center gap-4">
           <span className="uppercase tracking-widest font-black">LocalDiff.dev v3.2.0</span>
           <span className="text-emerald-500 bg-emerald-500/5 px-2 py-0.5 rounded">Secure</span>
        </div>
        <div className="flex items-center gap-4">
           <a href="/guide" className="hover:text-blue-500 transition-colors">Guide</a>
           <a href="/about" className="hover:text-blue-500 transition-colors">About</a>
           <a href="/privacy" className="hover:text-blue-500 transition-colors">Privacy</a>
           <a href="/terms" className="hover:text-blue-500 transition-colors">Terms</a>
           <span className="text-slate-600">|</span>
           <span>Data never leaves browser</span>
           <span>&copy; 2026 LocalDiff.dev</span>
        </div>
      </footer>
    </div>
  );
};

export default App;
